import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Paper,
  ActionIcon,
  ThemeIcon,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconDots,
  IconReceipt,
  IconCreditCard,
  IconBuildingBank,
  IconCash,
  IconTransfer,
  IconRefresh,
} from '@tabler/icons-react';
import { Card } from '../../ui';
import { FadeIn, StaggeredList } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface Transaction {
  id: string;
  type: 'income' | 'expense' | 'transfer';
  category: string;
  description: string;
  amount: number;
  currency?: string;
  date: Date;
  status?: 'pending' | 'completed' | 'failed' | 'cancelled';
  paymentMethod?: 'cash' | 'card' | 'bank' | 'transfer';
  reference?: string;
  account?: string;
  counterparty?: string;
  metadata?: Record<string, unknown>;
}

export interface TransactionListProps {
  transactions: Transaction[];
  groupByDate?: boolean;
  onTransactionClick?: (transaction: Transaction) => void;
  showBalance?: boolean;
  balance?: number;
  currency?: string;
  variant?: 'default' | 'compact';
  className?: string;
}

export const TransactionList = forwardRef<HTMLDivElement, TransactionListProps>(
  (
    {
      transactions,
      groupByDate = true,
      onTransactionClick,
      showBalance = false,
      balance = 0,
      currency = 'USD',
      variant = 'default',
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const formatCurrency = (amount: number, cur = currency) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cur,
      }).format(Math.abs(amount));
    };

    const formatDate = (date: Date, format: 'full' | 'short' = 'full') => {
      const d = new Date(date);
      if (format === 'short') {
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
      return d.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const getTypeIcon = (type: Transaction['type'], method?: Transaction['paymentMethod']) => {
      if (type === 'transfer') return <IconTransfer size={16} />;

      switch (method) {
        case 'card':
          return <IconCreditCard size={16} />;
        case 'bank':
          return <IconBuildingBank size={16} />;
        case 'cash':
          return <IconCash size={16} />;
        default:
          return <IconReceipt size={16} />;
      }
    };

    const getStatusColor = (status?: Transaction['status']) => {
      switch (status) {
        case 'completed':
          return 'green';
        case 'pending':
          return 'yellow';
        case 'failed':
          return 'red';
        case 'cancelled':
          return 'gray';
        default:
          return 'blue';
      }
    };

    // Group transactions by date
    const groupedTransactions = groupByDate
      ? transactions.reduce((groups, transaction) => {
          const dateKey = new Date(transaction.date).toDateString();
          if (!groups[dateKey]) {
            groups[dateKey] = [];
          }
          groups[dateKey].push(transaction);
          return groups;
        }, {} as Record<string, Transaction[]>)
      : { all: transactions };

    const renderTransaction = (transaction: Transaction) => {
      const isIncome = transaction.type === 'income';
      const isExpense = transaction.type === 'expense';
      const isTransfer = transaction.type === 'transfer';

      return (
        <Paper
          p={variant === 'compact' ? 'xs' : 'sm'}
          withBorder
          style={{ cursor: onTransactionClick ? 'pointer' : 'default' }}
          onClick={() => onTransactionClick?.(transaction)}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon
                size={variant === 'compact' ? 'sm' : 'md'}
                radius="xl"
                variant="light"
                color={isIncome ? 'green' : isExpense ? 'red' : 'blue'}
              >
                {getTypeIcon(transaction.type, transaction.paymentMethod)}
              </ThemeIcon>
              <Box style={{ overflow: 'hidden' }}>
                <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
                  {transaction.description}
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {transaction.category}
                  </Text>
                  {transaction.counterparty && (
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>• {transaction.counterparty}</Text>
                  )}
                </Group>
              </Box>
            </Group>
            <Group gap="sm" wrap="nowrap">
              <Box ta="right">
                <Group gap={2} justify="flex-end">
                  {isIncome && <IconArrowDownRight size={14} color="var(--mantine-color-green-6)" />}
                  {isExpense && <IconArrowUpRight size={14} color="var(--mantine-color-red-6)" />}
                  {isTransfer && <IconRefresh size={14} color="var(--mantine-color-blue-6)" />}
                  <Text
                    size="sm"
                    fw={600}
                    c={isIncome ? 'green' : isExpense ? 'red' : 'blue'}
                    style={accessibilityStyles}
                  >
                    {isExpense ? '-' : isIncome ? '+' : ''}
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </Text>
                </Group>
                {variant !== 'compact' && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatDate(transaction.date, 'short')}
                  </Text>
                )}
              </Box>
              {transaction.status && transaction.status !== 'completed' && (
                <Badge size="xs" color={getStatusColor(transaction.status)}>
                  {transaction.status}
                </Badge>
              )}
            </Group>
          </Group>
        </Paper>
      );
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {/* Balance */}
            {showBalance && (
              <Paper p="md" bg="gray.0" radius="md">
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>Current Balance</Text>
                    <Text size="xl" fw={700} c={balance >= 0 ? 'green' : 'red'} style={accessibilityStyles}>
                      {formatCurrency(balance)}
                    </Text>
                  </Box>
                  <Group gap="lg">
                    <Box ta="right">
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Income</Text>
                      <Text size="sm" fw={500} c="green" style={accessibilityStyles}>
                        +{formatCurrency(
                          transactions
                            .filter((t) => t.type === 'income')
                            .reduce((sum, t) => sum + t.amount, 0)
                        )}
                      </Text>
                    </Box>
                    <Box ta="right">
                      <Text size="xs" c="dimmed" style={accessibilityStyles}>Expenses</Text>
                      <Text size="sm" fw={500} c="red" style={accessibilityStyles}>
                        -{formatCurrency(
                          transactions
                            .filter((t) => t.type === 'expense')
                            .reduce((sum, t) => sum + t.amount, 0)
                        )}
                      </Text>
                    </Box>
                  </Group>
                </Group>
              </Paper>
            )}

            {/* Transactions */}
            {Object.entries(groupedTransactions).map(([dateKey, dateTransactions]) => (
              <Box key={dateKey}>
                {groupByDate && dateKey !== 'all' && (
                  <Text size="sm" fw={500} c="dimmed" mb="xs" style={accessibilityStyles}>
                    {formatDate(new Date(dateKey))}
                  </Text>
                )}
                <StaggeredList staggerDelay={30}>
                  <Stack gap="xs">
                    {dateTransactions.map((transaction) => (
                      <Box key={transaction.id}>{renderTransaction(transaction)}</Box>
                    ))}
                  </Stack>
                </StaggeredList>
              </Box>
            ))}

            {transactions.length === 0 && (
              <Box py="xl" ta="center">
                <Text c="dimmed" style={accessibilityStyles}>No transactions</Text>
              </Box>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

TransactionList.displayName = 'TransactionList';

export default TransactionList;
